'use client'

import { cn } from '@/lib/utils'
import { motion, AnimatePresence, Transition, Variants } from 'framer-motion'
import { useState, useEffect, Children, useRef, useCallback } from 'react'

type TextLoopProps = {
  children: React.ReactNode[]
  className?: string
  interval?: number
  transition?: Transition
  variants?: Variants
  onIndexChange?: (index: number) => void
  /** Ref to compare position with - if on different line, will center */
  compareRef?: React.RefObject<HTMLElement | null>
}

export function TextLoop({
  children,
  className,
  interval = 2,
  transition = { duration: 0.4, ease: 'easeInOut' },
  variants,
  onIndexChange,
  compareRef,
}: TextLoopProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isOnDifferentLine, setIsOnDifferentLine] = useState(false)
  const selfRef = useRef<HTMLSpanElement>(null)
  const items = Children.toArray(children)

  // Check if this element is on a different line than compareRef
  const checkLinePosition = useCallback(() => {
    if (!compareRef?.current || !selfRef.current) return

    const compareRect = compareRef.current.getBoundingClientRect()
    const selfRect = selfRef.current.getBoundingClientRect()

    // Calculate how many lines apart they are
    const lineHeight = selfRect.height
    const linesDiff = Math.round((selfRect.top - compareRect.top) / lineHeight)

    // Only center if on 3rd line or beyond (2+ lines difference)
    setIsOnDifferentLine(linesDiff >= 2)
  }, [compareRef])

  useEffect(() => {
    checkLinePosition()
    // Debounced resize handler to reduce CPU usage
    let timeoutId: ReturnType<typeof setTimeout>
    const debouncedCheck = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(checkLinePosition, 150)
    }
    window.addEventListener('resize', debouncedCheck, { passive: true })
    return () => {
      window.removeEventListener('resize', debouncedCheck)
      clearTimeout(timeoutId)
    }
  }, [checkLinePosition])

  useEffect(() => {
    const intervalMs = interval * 1000

    const timer = setInterval(() => {
      setCurrentIndex((current) => {
        const next = (current + 1) % items.length
        onIndexChange?.(next)
        return next
      })
    }, intervalMs)
    return () => clearInterval(timer)
  }, [items.length, interval, onIndexChange])

  const motionVariants: Variants = {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 },
  }

  return (
    <span ref={selfRef} className={cn('relative inline-block', className)}>
      {/* Elemento invisível para manter o espaço do maior item */}
      <span className="opacity-0">
        {items.reduce((longest, item) => {
          const itemStr = String((item as React.ReactElement<{ children?: React.ReactNode }>)?.props?.children || item)
          const longestStr = String((longest as React.ReactElement<{ children?: React.ReactNode }>)?.props?.children || longest)
          return itemStr.length > longestStr.length ? item : longest
        }, items[0])}
      </span>
      {/* Elemento animado posicionado absolutamente */}
      <AnimatePresence mode='popLayout' initial={false}>
        <motion.span
          key={currentIndex}
          initial='initial'
          animate='animate'
          exit='exit'
          transition={transition}
          variants={variants || motionVariants}
          className={cn(
            "absolute inset-0 flex items-center",
            isOnDifferentLine ? "justify-center" : "justify-start"
          )}
        >
          {items[currentIndex]}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}
